<template>
    <li
        ref="itemRef"
        class="ui-context-menu-item"
        :class="{
            'ui-context-menu-item--danger': item.danger && !item.disabled,
            'ui-context-menu-item--normal': !item.danger && !item.disabled,
            'ui-context-menu-item--disabled': item.disabled,
            'ui-context-menu-item--has-children': hasChildren,
            'ui-context-menu-item--active': isFocused,
        }"
        :role="hasChildren ? 'menuitem' : 'menuitem'"
        :aria-haspopup="hasChildren ? 'true' : undefined"
        :aria-expanded="hasChildren && isSubmenuOpen ? 'true' : 'false'"
        :aria-disabled="item.disabled ? 'true' : 'false'"
        :tabindex="item.disabled ? -1 : computedTabindex"
        :data-item-id="itemId"
        :data-level="level"
        @click="handleClick"
        @mouseenter="handleMouseEnter"
        @mouseleave="handleMouseLeave"
        @touchstart.prevent="handleTouchStart"
        @keydown="handleKeyDown"
        @focus="handleFocus"
        @blur="handleBlur"
    >
        <Icon v-if="item.icon" :icon="item.icon" class="ui-context-menu-item-icon"></Icon>
        <span class="ui-context-menu-item-label">{{ item.label }}</span>
        <Icon v-if="hasChildren" icon="hero/outline/chevron-right" class="ui-context-menu-item-chevron"></Icon>

        <!-- Nested Submenu (Teleport to body for z-index management) -->
        <Teleport v-if="hasChildren && canNest && isSubmenuOpen" to="body">
            <div
                ref="submenuRef"
                class="ui-context-submenu ui-context-submenu-positioned"
                :data-level="level + 1"
                :style="submenuStyle"
                @mouseenter="handleSubmenuMouseEnter"
                @mouseleave="handleSubmenuMouseLeave"
            >
                <ul class="ui-context-menu" :class="[`ui-context-menu--level-${level + 1}`]" role="menu">
                    <ContextMenuItem
                        v-for="(child, childIndex) in item.children"
                        :key="`${itemId}-${childIndex}`"
                        :item="child"
                        :index="childIndex"
                        :level="level + 1"
                        :parent-id="itemId"
                        :max-depth="maxDepth"
                        @close-all="$emit('close-all')"
                    />
                </ul>
            </div>
        </Teleport>
    </li>
</template>

<script setup lang="ts">
import { ref, computed, nextTick, inject, watch } from 'vue';
import type { Ref } from 'vue';
import { computePosition, flip, shift, offset } from '@floating-ui/dom';
import type { ContextMenuItem as ContextMenuItemType } from './ContextMenu.vue';
import { useContextMenuNavigation, type NavigationState, type HoverState } from './useContextMenuNavigation';

interface Props {
    item: ContextMenuItemType;
    index: number;
    level: number;
    parentId?: string;
    maxDepth?: number;
}

const props = withDefaults(defineProps<Props>(), {
    parentId: '',
    maxDepth: 5,
});

const emit = defineEmits<{
    (event: 'close-all'): void;
}>();

const itemRef = ref<HTMLElement | null>(null);
const submenuRef = ref<HTMLElement | null>(null);
const submenuPosition = ref({ x: 0, y: 0 });
const submenuReady = ref(false);
const isFocused = ref(false);
const touchStartTime = ref(0);

// Inject navigation and hover state
const navigationState = inject<Ref<NavigationState>>('navigationState')!;
const hoverState = inject<Ref<HoverState>>('hoverState')!;
const setFocusedItemId = inject<(id: string) => void>('setFocusedItemId', () => {});
const toggleSubmenu = inject<(id: string, open: boolean) => void>('toggleSubmenu')!;

// Navigation composable
const navigation = useContextMenuNavigation(navigationState);

const itemId = computed(() => {
    return props.parentId ? `${props.parentId}-${props.index}` : `${props.index}`;
});

const hasChildren = computed(() => {
    return props.item.children && props.item.children.length > 0;
});

const canNest = computed(() => {
    return props.level < props.maxDepth;
});

const isSubmenuOpen = computed(() => {
    return navigationState.value.openSubmenus.has(itemId.value);
});

const computedTabindex = computed(() => {
    if (props.item.disabled) return -1;
    if (navigationState.value.focusedId === itemId.value) return 0;
    if (navigationState.value.focusedId === null && props.index === 0) return 0;
    return -1;
});

const baseZIndex = inject<number>('contextMenuZIndex', 50);

const submenuStyle = computed(() => {
    return {
        position: 'fixed' as const,
        top: `${submenuPosition.value.y}px`,
        left: `${submenuPosition.value.x}px`,
        zIndex: baseZIndex + props.level + 1,
        visibility: submenuReady.value ? ('visible' as const) : ('hidden' as const),
        opacity: submenuReady.value ? 1 : 0,
        transition: submenuReady.value ? 'opacity 150ms ease-out' : 'none',
    };
});

/**
 * Calculate submenu position using Floating UI
 */
const calculateSubmenuPosition = async () => {
    if (!itemRef.value || !submenuRef.value) {
        return;
    }

    // Wait for Teleport to complete
    await nextTick();
    await nextTick();

    try {
        const { x, y } = await computePosition(itemRef.value, submenuRef.value, {
            placement: 'right-start',
            middleware: [
                offset(2),
                flip({
                    fallbackPlacements: ['left-start', 'right-end', 'left-end'],
                }),
                shift({
                    padding: 8,
                }),
            ],
        });

        submenuPosition.value = { x, y };
        submenuReady.value = true;
    } catch (error) {
        console.error('[ContextMenu] Error calculating submenu position:', error);
        submenuReady.value = true; // Show anyway to avoid permanent hiding
    }
};

/**
 * Mouse enter handler - update hover path and open submenu
 */
const handleMouseEnter = () => {
    if (props.item.disabled) return;

    // Build ancestor path including this item
    const path = navigation.buildAncestorPath(itemId.value);
    hoverState.value.hoveredPath = path;

    // Clear ALL timers globally
    hoverState.value.activeTimers.forEach((timerId) => {
        clearTimeout(timerId);
    });
    hoverState.value.activeTimers.clear();

    // Close all sibling submenus (items at same level)
    const currentItem = navigationState.value.flatIndex.get(itemId.value);
    if (currentItem) {
        const siblings = currentItem.parentId
            ? navigationState.value.childrenMap.get(currentItem.parentId) || []
            : navigationState.value.rootIds;

        siblings.forEach((siblingId) => {
            if (siblingId !== itemId.value && navigationState.value.openSubmenus.has(siblingId)) {
                toggleSubmenu(siblingId, false);
            }
        });
    }

    // Open submenu if has children
    if (hasChildren.value && canNest.value) {
        toggleSubmenu(itemId.value, true);
    }
};

/**
 * Mouse leave handler - start close timer
 */
const handleMouseLeave = () => {
    if (!hasChildren.value) return;

    const timerId = window.setTimeout(() => {
        // Only close if this item is no longer in hover path
        if (!hoverState.value.hoveredPath.includes(itemId.value)) {
            toggleSubmenu(itemId.value, false);
            hoverState.value.activeTimers.delete(itemId.value);
        }
    }, 300);

    hoverState.value.activeTimers.set(itemId.value, timerId);
};

/**
 * Submenu mouse enter - clear timer for this item
 */
const handleSubmenuMouseEnter = () => {
    const timerId = hoverState.value.activeTimers.get(itemId.value);
    if (timerId) {
        clearTimeout(timerId);
        hoverState.value.activeTimers.delete(itemId.value);
    }
};

/**
 * Submenu mouse leave - start close timer
 */
const handleSubmenuMouseLeave = () => {
    handleMouseLeave();
};

/**
 * Click handler
 */
const handleClick = (event: MouseEvent) => {
    if (props.item.disabled) {
        event.preventDefault();
        event.stopPropagation();
        return;
    }

    // On desktop: items with children don't close menu on click
    if (hasChildren.value) {
        event.stopPropagation();
        return;
    }

    // Execute action and close menu
    if (props.item.action) {
        props.item.action();
    }

    emit('close-all');
};

/**
 * Touch handler for mobile devices
 */
const handleTouchStart = () => {
    if (props.item.disabled) return;

    touchStartTime.value = Date.now();

    if (hasChildren.value && canNest.value) {
        const isOpen = isSubmenuOpen.value;
        toggleSubmenu(itemId.value, !isOpen);
    } else {
        if (props.item.action) {
            props.item.action();
        }
        emit('close-all');
    }
};

/**
 * Keyboard navigation handler using data-driven navigation
 */
const handleKeyDown = (event: KeyboardEvent) => {
    if (props.item.disabled) return;

    const currentId = itemId.value;

    switch (event.key) {
        case 'ArrowRight': {
            event.preventDefault();
            event.stopPropagation();

            if (hasChildren.value && canNest.value) {
                toggleSubmenu(currentId, true);

                // Wait for submenu to render and position before focusing
                nextTick(() => {
                    setTimeout(() => {
                        const firstChild = navigation.navigateIntoSubmenu(currentId);
                        if (firstChild) {
                            navigation.focusItemById(firstChild.id);
                            setFocusedItemId(firstChild.id);
                        }
                    }, 100);
                });
            }
            break;
        }

        case 'ArrowLeft': {
            event.preventDefault();
            event.stopPropagation();

            const parent = navigation.navigateToParent(currentId);
            if (parent) {
                toggleSubmenu(parent.id, false);
                navigation.focusItemById(parent.id);
                setFocusedItemId(parent.id);
            } else {
                emit('close-all');
            }
            break;
        }

        case 'ArrowDown': {
            event.preventDefault();
            event.stopPropagation();

            const nextItem = navigation.findNextItem(currentId);
            if (nextItem) {
                navigation.focusItemById(nextItem.id);
                setFocusedItemId(nextItem.id);
            }
            break;
        }

        case 'ArrowUp': {
            event.preventDefault();
            event.stopPropagation();

            const prevItem = navigation.findPrevItem(currentId);
            if (prevItem) {
                navigation.focusItemById(prevItem.id);
                setFocusedItemId(prevItem.id);
            }
            break;
        }

        case 'Home': {
            event.preventDefault();
            event.stopPropagation();

            const firstItem = navigation.findFirstInLevel(currentId);
            if (firstItem) {
                navigation.focusItemById(firstItem.id);
                setFocusedItemId(firstItem.id);
            }
            break;
        }

        case 'End': {
            event.preventDefault();
            event.stopPropagation();

            const lastItem = navigation.findLastInLevel(currentId);
            if (lastItem) {
                navigation.focusItemById(lastItem.id);
                setFocusedItemId(lastItem.id);
            }
            break;
        }

        case 'Enter':
        case ' ': {
            event.preventDefault();
            event.stopPropagation();

            if (hasChildren.value && canNest.value) {
                const isOpen = isSubmenuOpen.value;
                toggleSubmenu(currentId, !isOpen);
            } else {
                if (props.item.action) {
                    props.item.action();
                }
                emit('close-all');
            }
            break;
        }

        case 'Escape': {
            event.preventDefault();
            event.stopPropagation();
            emit('close-all');
            break;
        }
    }
};

/**
 * Focus handler
 */
const handleFocus = () => {
    isFocused.value = true;
    setFocusedItemId(itemId.value);
};

/**
 * Blur handler
 */
const handleBlur = () => {
    isFocused.value = false;
};

/**
 * Watch submenu open state to calculate position before showing
 */
watch(isSubmenuOpen, async (isOpen) => {
    if (isOpen) {
        submenuReady.value = false;

        await nextTick();
        await calculateSubmenuPosition();
    } else {
        submenuReady.value = false;
    }
});
</script>
